"use client";

import React, { useState, useEffect, useRef } from "react";
import { MonitorPlay, Scissors, Film, WandSparkles, Share2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
const stages = [{
  id: 1,
  title: "TRACY VETTER- AUTHOR",
  description: "Developing the narrative core of The Poseidon Project, translating the thriller's depth into a book - Tracy Vetter is the author of the book and the film's executive producer.",
  icon: <MonitorPlay className="w-6 h-6" />,
  idClassName: "relative z-10 w-full h-full flex items-center justify-center p-6 border border-[#00f2ff]/10 bg-[#03070b]/40 bg-cover bg-center bg-[url(https://spectre.10web.cloud/wp-content/uploads/2026/04/tracy.jpg)]"
}, {
  id: 2,
  title: "JORDAN BRANCH- SCREENPLAY AND VISUAL EFFECTS",
  description: "Adapted the book into a cinematic screenplay and storyboard to bring the high-stakes world of Tracy Vetter's vision to life.",
  icon: <Scissors className="w-6 h-6" />,
  idClassName: "bg-cover bg-center bg-[url(https://spectre.10web.cloud/wp-content/uploads/2026/04/jordan-director-scaled.png)]"
}, {
  id: 3,
  title: "SOUND EFFECTS & SCORING",
  description: "Layering atmospheric pressure sounds and a pulse-pounding score to finalize the immersive thriller experience.\n\n",
  icon: <Film className="w-6 h-6" />,
  idClassName: "relative z-10 w-full h-full flex items-center justify-center p-6 border border-[#00f2ff]/10 bg-[#03070b]/40 bg-cover bg-center bg-[url(https://spectre.10web.cloud/wp-content/uploads/2026/04/randy-huggins.png)]"
}];
export default function WorkflowTimeline() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const [activeStage, setActiveStage] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionHeight = rect.height;
      const startOffset = windowHeight * 0.5;
      const scrolled = -rect.top + startOffset;
      let percentage = scrolled / (sectionHeight - windowHeight * 0.5) * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
      const currentStageIndex = Math.floor(percentage / 100 * stages.length);
      setActiveStage(Math.min(currentStageIndex, stages.length - 1));
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="56133" id="production-timeline" ref={sectionRef} className="dark bg-[#03070b] text-[#e0e7ff] relative py-24 overflow-hidden">
      {}
      <div className="fixed top-0 left-0 right-0 z-50 w-full bg-[#03070b]/80 backdrop-blur-md border-b border-[#00f2ff]/20 px-4 py-2 transition-all duration-300">
        <div className="container mx-auto max-w-4xl flex items-center gap-4">
          <span className="text-xs font-mono text-[#00f2ff] uppercase tracking-widest">System Progress</span>
          <Progress value={progress} className="h-1.5 flex-1 bg-[#1e293b]/30" />
          <span className="text-xs font-mono text-[#94a3b8]">{Math.round(progress)}%</span>
        </div>
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold tracking-widest mb-4 bg-gradient-to-r from-[#00f2ff] to-[#0088ff] bg-clip-text text-transparent uppercase font-sans">
            Film Production Log
          </h2>
          <p className="text-[#94a3b8] max-w-2xl mx-auto text-lg font-sans">
            Tracking the evolution of The Poseidon Project from classified documents to cinematic reality.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-1 md:-translate-x-1/2 h-full bg-[#1e293b]/40 rounded-full overflow-hidden">
            {}
            <div className="w-full bg-[#00f2ff] shadow-[0_0_20px_2px_#00f2ff] transition-all duration-100 ease-linear" style={{
            height: `${progress}%`
          }} />
          </div>

          {}
          <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-[#00f2ff] rounded-full shadow-[0_0_15px_4px_#00f2ff] z-10 md:-translate-x-1/2 transition-all duration-100 ease-linear hidden md:block" style={{
          top: `${progress}%`,
          marginTop: '-8px'
        }} />

          {}
          <div className="space-y-24 relative z-10 pb-24">
            {stages.map((stage, index) => {
            const isEven = index % 2 === 0;
            const isActive = index <= activeStage;
            return <div key={stage.id} data-index={index} className={`relative flex flex-col md:flex-row items-center gap-8 md:gap-16 ${isEven ? "md:flex-row-reverse" : ""}`}>
                  {}
                  <div className={`absolute left-4 md:left-1/2 w-4 h-4 rounded-full border-2 z-20 md:-translate-x-1/2 transition-all duration-500 ${isActive ? "bg-[#03070b] border-[#00f2ff] shadow-[0_0_10px_#00f2ff] scale-125" : "bg-[#03070b] border-[#94a3b8]/30"}`}></div>

                  {}
                  <div className="w-full md:w-1/2 pl-12 md:pl-0">
                    <Card className={`
                      border-[#1e293b]/40 bg-[#08121d]/60 backdrop-blur-xl overflow-hidden transition-all duration-700 group
                      ${isActive ? "border-[#00f2ff]/50 shadow-[0_0_30px_-10px_#00f2ff] translate-y-0 opacity-100" : "translate-y-8 opacity-50 grayscale"}
                    `}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between mb-2">
                          <div className={`p-2 rounded-sm ${isActive ? "bg-[#00f2ff]/20 text-[#00f2ff]" : "bg-[#1e293b]/20 text-[#94a3b8]"}`}>
                            {stage.icon}
                          </div>
                          <span className="text-xs font-mono text-[#00f2ff]/60 tracking-tighter">PHASE 0{stage.id}</span>
                        </div>
                        <CardTitle className="text-xl md:text-2xl font-sans uppercase tracking-wider">{stage.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base leading-relaxed text-[#94a3b8] font-sans">
                          {stage.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </div>

                  {}
                  <div className="w-full md:w-1/2 pl-12 md:pl-0 flex justify-center">
                    <div className={`
                      relative w-full max-w-xs aspect-auto min-h-[300px] rounded-sm overflow-hidden transition-all duration-1000 delay-100
                      ${isActive ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-90 rotate-3"}
                    `}>
                      {}
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#00f2ff]/20 to-transparent opacity-50 blur-3xl"></div>
                      
                      {}
                      <div className={`${stage.idClassName || ""} relative z-10 w-full h-full flex items-center justify-center p-6 border border-[#00f2ff]/10 bg-[#03070b]/40`}>
                        <div className="text-center text-[#94a3b8]">
                          
                        </div>
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-12 text-center">
          <Button size="lg" className="rounded-sm px-10 text-lg h-14 bg-[#00f2ff] text-[#03070b] hover:bg-[#00d8e6] font-sans uppercase tracking-widest shadow-[0_0_20px_-5px_#00f2ff] transition-all duration-300" asChild>
            <Link to="#">
              Access Full Archive <MonitorPlay className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}